import { useState, useMemo } from 'react';

const LoyaltyCalculator = () => {
  const [spend, setSpend] = useState(5000);
  const [level, setLevel] = useState<'standard' | 'gold'>('standard');
  const [useApp, setUseApp] = useState(false);

  const cashbackRate = level === 'gold' ? 3 : 1;
  const appBonus = level === 'gold' && useApp ? 5 : 0;
  const totalRate = cashbackRate + appBonus;

  const result = useMemo(() => {
    const earned = Math.floor(spend * totalRate / 100);
    const withBirthday = earned + (level === 'gold' ? 150 : 100);
    const withWelcome = withBirthday + 100;
    return { earned, withBirthday, withWelcome, totalRate };
  }, [spend, level, totalRate]);

  const isGoldEligible = spend >= 10000;

  return (
    <section className="py-20 px-4 md:px-8 lg:px-12" style={{ background: '#070710' }}>
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-[0.3em] mb-2" style={{ color: '#ff2d78' }}>КАЛЬКУЛЯТОР</p>
          <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white mb-3">
            ПОСЧИТАЙТЕ ВЫГОДУ
          </h2>
          <p className="font-mono text-sm text-white/40">
            Узнайте, сколько баллов вы заработаете за свои покупки
          </p>
        </div>

        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,45,120,0.2)', background: 'rgba(255,45,120,0.03)' }}>
          <div className="p-8">

            {/* Level selector */}
            <div className="mb-8">
              <label className="font-mono text-xs text-white/40 tracking-widest mb-3 block">УРОВЕНЬ КАРТЫ</label>
              <div className="flex gap-3">
                {[
                  { id: 'standard', label: 'Стандартный', color: '#00f5ff', icon: 'ri-vip-crown-line' },
                  { id: 'gold', label: 'Золотой', color: '#f0ff00', icon: 'ri-vip-crown-fill' },
                ].map(l => (
                  <button
                    key={l.id}
                    onClick={() => setLevel(l.id as 'standard' | 'gold')}
                    className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
                    style={level === l.id
                      ? { background: `${l.color}15`, border: `2px solid ${l.color}`, color: l.color, boxShadow: `0 0 15px ${l.color}30` }
                      : { background: 'rgba(255,255,255,0.03)', border: '2px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }
                    }
                  >
                    <i className={l.icon} />
                    {l.label}
                  </button>
                ))}
              </div>
              {level === 'standard' && isGoldEligible && (
                <p className="font-mono text-[11px] mt-2" style={{ color: '#f0ff00' }}>
                  <i className="ri-star-line mr-1" />
                  При такой сумме покупок вы уже можете получить Золотой уровень!
                </p>
              )}
            </div>

            {/* Spend slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="font-mono text-xs text-white/40 tracking-widest">СУММА ПОКУПОК В МЕСЯЦ</label>
                <div className="font-orbitron font-black text-xl" style={{ color: '#ff2d78' }}>
                  {spend.toLocaleString()} ₽
                </div>
              </div>
              <input
                type="range"
                min={500}
                max={50000}
                step={500}
                value={spend}
                onChange={e => setSpend(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #ff2d78 0%, #ff2d78 ${(spend - 500) / (50000 - 500) * 100}%, rgba(255,255,255,0.1) ${(spend - 500) / (50000 - 500) * 100}%, rgba(255,255,255,0.1) 100%)`,
                }}
              />
              <div className="flex justify-between font-mono text-[10px] text-white/25 mt-1">
                <span>500 ₽</span>
                <span>50 000 ₽</span>
              </div>
            </div>

            {/* App toggle (gold only) */}
            {level === 'gold' && (
              <div className="mb-8 flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.15)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.25)' }}>
                    <i className="ri-smartphone-line" style={{ color: '#39ff14', fontSize: '16px' }} />
                  </div>
                  <div>
                    <div className="font-mono text-sm text-white/80">Заказы через приложение</div>
                    <div className="font-mono text-[11px] text-white/35">+5% дополнительно для Золотого уровня</div>
                  </div>
                </div>
                <button
                  onClick={() => setUseApp(v => !v)}
                  className="relative w-12 h-6 rounded-full cursor-pointer transition-all duration-300 shrink-0"
                  style={{ background: useApp ? '#39ff14' : 'rgba(255,255,255,0.1)', boxShadow: useApp ? '0 0 10px rgba(57,255,20,0.5)' : 'none' }}
                >
                  <div
                    className="absolute top-1 w-4 h-4 rounded-full transition-all duration-300"
                    style={{ background: useApp ? '#050508' : 'rgba(255,255,255,0.4)', left: useApp ? '28px' : '4px' }}
                  />
                </button>
              </div>
            )}

            {/* Results */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl text-center" style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)' }}>
                <div className="font-mono text-[10px] text-white/35 mb-2 tracking-widest">КЭШБЭК {totalRate}%</div>
                <div className="font-orbitron font-black text-3xl mb-1" style={{ color: '#ff2d78', textShadow: '0 0 20px rgba(255,45,120,0.6)' }}>
                  {result.earned}
                </div>
                <div className="font-mono text-xs text-white/40">баллов за покупки</div>
              </div>

              <div className="p-5 rounded-2xl text-center" style={{ background: 'rgba(240,255,0,0.06)', border: '1px solid rgba(240,255,0,0.15)' }}>
                <div className="font-mono text-[10px] text-white/35 mb-2 tracking-widest">+ ДЕНЬ РОЖДЕНИЯ</div>
                <div className="font-orbitron font-black text-3xl mb-1" style={{ color: '#f0ff00', textShadow: '0 0 20px rgba(240,255,0,0.5)' }}>
                  {result.withBirthday}
                </div>
                <div className="font-mono text-xs text-white/40">баллов в месяц рождения</div>
              </div>

              <div className="p-5 rounded-2xl text-center" style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.15)' }}>
                <div className="font-mono text-[10px] text-white/35 mb-2 tracking-widest">+ ПРИВЕТСТВИЕ</div>
                <div className="font-orbitron font-black text-3xl mb-1" style={{ color: '#39ff14', textShadow: '0 0 20px rgba(57,255,20,0.5)' }}>
                  {result.withWelcome}
                </div>
                <div className="font-mono text-xs text-white/40">баллов в первый месяц</div>
              </div>
            </div>

            <div className="mt-5 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <i className="ri-information-line text-white/30 shrink-0" />
              <p className="font-mono text-[11px] text-white/35 leading-relaxed">
                1 балл = 1 рубль. Оплачивайте баллами до 99% стоимости любого товара. Баллы действуют 30 дней с момента начисления на покупку от 500 ₽.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoyaltyCalculator;
